import { useEffect } from 'react'

import { useProductDetails } from './useProductDetails'

/**
 * Hook to dynamically update favicon, manifest, and page title based on product branding
 *
 * Updates the browser tab icon, PWA manifest, and page title to match the
 * currently active brand (Tempus Tools by default, or a reseller brand).
 *
 * **What gets updated:**
 * - Browser favicons (light and dark mode)
 * - PWA manifest file (controls app name when installed on home screen)
 * - Page title (browser tab title)
 *
 * **Light/Dark Mode Favicon Support:**
 * - Updates both light mode (`favicon-light`) and dark mode (`favicon-dark`) favicons
 * - Respects user's system color scheme preference via `prefers-color-scheme`
 * - Falls back to light mode favicon if dark version doesn't exist
 *
 * **Expected file structure:** `{brand}/favicon.ico`, `{brand}/favicon-dark.ico`,
 * `{brand}/manifest.json` (e.g. `/tempustools/favicon.ico`, `/libellula/favicon.ico`).
 *
 * This hook should be called in a top-level component after authentication
 * (e.g., Layout.jsx) to ensure all branding elements reflect the resolved brand.
 *
 * @example
 * const Layout = () => {
 *   useFaviconUpdater() // Updates favicons, manifest, and title based on brand
 *   return <div>...</div>
 * }
 */
export function useFaviconUpdater() {
    const productDetails = useProductDetails()

    useEffect(() => {
        const basePath = `${productDetails.name}/`
        const lightFaviconPath = `${basePath}favicon.ico`
        const darkFaviconPath = `${basePath}favicon-dark.ico`
        const manifestPath = `/${basePath}manifest.json`

        const faviconElement = document.getElementById('favicon')
        if (faviconElement && 'href' in faviconElement) {
            faviconElement.href = lightFaviconPath
        }

        const faviconLightElement = document.getElementById('favicon-light')
        if (faviconLightElement && 'href' in faviconLightElement) {
            faviconLightElement.href = lightFaviconPath
        }

        const faviconDarkElement = document.getElementById('favicon-dark')
        if (faviconDarkElement && 'href' in faviconDarkElement) {
            faviconDarkElement.href = darkFaviconPath
        }

        const manifestElement = document.getElementById('manifest')
        if (manifestElement && 'href' in manifestElement) {
            manifestElement.href = manifestPath
        }

        document.title = productDetails.title
    }, [productDetails])
}
